import { PageHero, SitePage } from "@/components/SitePage";
import HomeClosingCTA from "@/components/HomeClosingCTA";

const metadata = [
  ["Sector", "Diagnostics & outpatient care"],
  ["Client", "A diagnostics chain · 15 centres across India"],
  ["Scope", "Inbound voice agent, 24/7"],
  ["Stage", "Live in production"],
];

const stats = [
  ["~2,000", "calls handled every day"],
  ["72%", "resolved without a human"],
  ["65%", "booking-intent calls converted"],
  ["10 weeks", "from kickoff to live"],
];

const sections = [
  ["01", "Operating requirement", [
    "A diagnostics chain runs 15 diagnostics and outpatient centres across India. Its phone lines carry four kinds of demand at once: home-collection bookings, outpatient appointment bookings, test-report queries and general questions about timings, pricing and preparation. Calls arrive around the clock in Hindi, English and Bengali.",
    "Every one of those calls begins as an unstructured conversation but must end as a verified action: a registered booking, an accurate report status or a correct answer. Anything less creates rework for the branch teams and a lost patient.",
  ]],
  ["02", "System design", [
    "Averentia built a single voice agent that identifies the intent on each call and completes it. For home collection it captures the patient, address, requested tests and slot, then registers the booking. For outpatient visits it books the doctor, centre and time. For report queries it looks up the status and reads it back. For general queries it answers from the centre catalogue.",
    "Where a call cannot be completed by the agent, it is transferred to an operator within the same service path with the context already captured, so completed calls and exceptions remain part of one controlled process rather than separate channels.",
  ]],
  ["03", "Operational delivery", [
    "The agent went live in ten weeks and now handles roughly 2,000 calls a day, 24/7, across all three languages. It resolves 72% of calls without a human and converts 65% of booking-intent calls into confirmed bookings.",
    "Completion, fallback and failure events are reported per intent and per centre, giving the operating team a clear view of how calls move through the system and where intervention is required.",
  ]],
];

export default function CaseStudy() {
  return (
    <SitePage>
      <PageHero
        label="Case study"
        title="Voice agents for a diagnostics chain."
        intro="A production voice agent answering roughly 2,000 patient calls a day in Hindi, English and Bengali: booking home collections and outpatient appointments, answering test-report and general queries, and handing exceptions to operators."
        aside={
          <figure className="av-page-image">
            <img src="/case-studies/healthcare-lab.jpg" alt="Diagnostic laboratory technician operating analysis equipment" />
            <figcaption>Photo: Tima Miroshnichenko / Pexels</figcaption>
          </figure>
        }
      >
        <a className="av-back-link" href="/work">← All work</a>
      </PageHero>
      <section className="av-case-metadata" aria-label="Project information">
        <div className="av-shell av-metadata-grid">
          {metadata.map(([label, value]) => (
            <div key={label}><span>{label}</span><strong>{value}</strong></div>
          ))}
        </div>
      </section>
      <section className="av-case-stats" aria-label="Results">
        <div className="av-shell av-stats-grid">
          {stats.map(([value, label]) => (
            <div key={label}><strong>{value}</strong><span>{label}</span></div>
          ))}
        </div>
      </section>
      <section className="av-section av-page-section">
        <div className="av-shell av-case-layout">
          <aside>
            <p className="av-section-label"><span /> Engagement scope</p>
            <p>Home-collection booking, outpatient appointment booking, test-report queries, general queries and operator fallback in one 24/7 multilingual voice agent.</p>
          </aside>
          <div className="av-case-body">
            {sections.map(([number, title, paragraphs]) => (
              <article key={number}>
                <span className="av-index">{number}</span>
                <h2>{title}</h2>
                {paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </article>
            ))}
          </div>
        </div>
      </section>
      <HomeClosingCTA />
    </SitePage>
  );
}
